import { FC, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { QuestionOutlined } from '@ant-design/icons';
import { Menu, Tooltip } from 'antd';
import { MenuItemType } from 'antd/lib/menu/hooks/useItems';
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import dayjs from 'dayjs';
import DynamicMenu, { DynamicMenuProps } from './components/DynamicMenu';
import SideMenu from './components/SideMenu';
import TabContent from './components/TabContent';
import PromoBanner from '../PromoBanner/PromoBanner';
import styles from './Nav.module.scss';
import { DatePeriodType, TabType } from './types';
import {
  DynamicTabs,
  MenuTab,
} from '../../../../app/providers/redux/slices/app/types';
import { fetchTags } from '../../../../app/providers/redux/slices/reports/params/reports/reportsSlice';
import { ROLES } from '../../../../helpers/utils/auth';
import { isImportBlockedByTariff } from '../../../../helpers/utils/tariff';
import { showInfoNotification } from '../../../../helpers/utils/ui';
import {
  QUERY_FROM_MENU,
  QUERY_NAME_DASHBOARD,
} from '../../constants/query-params';
import { routeNames } from '../../constants/routes';
import { useActions } from '../../hooks/useActions';
import { useTypedSelector, useAppDispatch } from '../../hooks/useTypedSelector';

type ActiveContentTab = 'all' | 'my' | 'schools' | 'scan';

const pathToKeyMap: [string, MenuTab][] = [
  [routeNames.schoolStaff, 'settings'],
  [routeNames.adsSpend, 'ads'],
  [routeNames.adsConnect, 'ads'],
  [routeNames.adsRules, 'ads'],
  [routeNames.orderReports, 'reports'],
  [routeNames.paymentReports, 'reports'],
  [routeNames.adReports, 'reports'],
  [routeNames.webinarReports, 'reports'],
  [routeNames.yandexMetrics, 'tools'],
  [routeNames.schoolEdit, 'settings'],
  [routeNames.schoolTrackScript, 'settings'],
  [routeNames.schoolLoadData, 'bd'],
  [routeNames.export, 'bd'],
  [routeNames.partner, 'suppliers'],
  [routeNames.dashboards, 'dashboards'],
  [routeNames.home, 'reports'],
];

const isDynamicTab = (tab: MenuTab): tab is DynamicTabs =>
  tab === 'ads' ||
  tab === 'bd' ||
  tab === 'settings' ||
  tab === 'tools';
const getBdLinks = (activeTab: TabType, isContact?: boolean) => {
  const contactPermission = isContact
    ? [ROLES.owner, ROLES.employee]
    : [ROLES.owner];
  switch (activeTab) {
    case 'webinars':
      return [
        {
          key: 'webinarList',
          label: 'Список вебинаров',
          url: routeNames.webinarList,
          permission: contactPermission,
        },
        {
          key: 'reportWebinarComments',
          label: 'Комментарии',
          url: routeNames.reportWebinarComments,
          permission: contactPermission,
        },
        {
          key: 'reportWebinarAttendance',
          label: 'Посещение вебинара',
          url: routeNames.reportWebinarAttendance,
          permission: contactPermission,
        },
      ];
    case 'visits':
      return [
        {
          key: 'reportSession',
          label: 'Сессии',
          url: routeNames.reportSession,
          permission: contactPermission,
        },
        {
          key: 'reportHit',
          label: 'Хиты',
          url: routeNames.reportHit,
          permission: contactPermission,
        },
        {
          key: 'reportVisitors',
          label: 'Посетители',
          url: routeNames.reportVisitors,
          permission: contactPermission,
        },
        {
          key: 'reportHitStat',
          label: 'Статистика по экранам',
          url: routeNames.reportHitStat,
          permission: contactPermission,
        },
      ];
    case 'platform':
    default:
      return [
        {
          key: 'Users',
          label: 'Пользователи',
          url: routeNames.reportUsers,
          permission: contactPermission,
        },
        {
          key: 'reportRegs',
          label: 'Регистрации',
          url: routeNames.reportRegs,
          permission: contactPermission,
        },
        {
          key: 'reportOrders',
          label: 'Заказы',
          url: routeNames.reportOrders,
          permission: contactPermission,
        },
        {
          key: 'reportPayments',
          label: 'Платежи',
          url: routeNames.reportPayments,
          permission: contactPermission,
        },
      ];
  }
};

const getAdsLinks = (activeTab: TabType) => {
  switch (activeTab) {
    case 'announcements':
      return [
        {
          key: 'auto-placement-ads',
          label: 'Автоматическое размещение',
          url: `${routeNames.adsConnect}?tab=2`,
        },
        {
          key: 'manual-placement-ads',
          label: 'Ручное размещение',
          url: `${routeNames.adsSpend}?tab=2`,
        },
        {
          key: 'add-ad-spend',
          label: 'Добавить расход',
          url: `${routeNames.adsSpend}?tab=2&showModal=true`,
        },
      ];
    case 'cabinets':
    default:
      return [
        {
          key: 'auto-placement-accounts',
          label: 'Автоматическое размещение',
          url: `${routeNames.adsConnect}?tab=1`,
        },
        {
          key: 'manual-placement-accounts',
          label: 'Ручное размещение',
          url: `${routeNames.adsSpend}?tab=1`,
        },
      ];
  }
};

const getIntegrationLinks = (activeTab: TabType) => {
  switch (activeTab) {
    case 'integrations':
      return [
        {
          key: 'schoolAdd',
          label: 'GetCourse',
          url: routeNames.schoolAdd,
        },
        {
          key: 'adsConnect',
          label: 'Рекламные кабинеты',
          url: routeNames.adsConnect,
        },
        {
          key: 'yandexMetrics',
          label: 'Яндекс метрика',
          url: `${routeNames.yandexMetrics}?tab=2`,
        },
        {
          key: 'webinarConnectBizon',
          label: 'Бизон 365',
          url: routeNames.webinarConnectBizon,
          permission: [ROLES.owner, ROLES.employee],
        },
      ];
    default:
      return null;
  }
};

const HelpWrapper: FC<{ children: ReactNode }> = ({ children }) => (
  <span
    style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}
  >
    <QuestionOutlined
      style={{
        background: '#f0f0f0',
        color: '#999',
        borderRadius: '50%',
        padding: 4,
        fontSize: 11,
        flexShrink: 0,
      }}
    />
    {children}
  </span>
);

const Nav: FC<{ collapsed: boolean }> = ({ collapsed }) => {
  const { activeMainTab } = useTypedSelector((state) => state.app);
  const {
    savedReports,
    savedDashboards,
    mainDashboardHash,
    tags,
  } = useTypedSelector((state) => state.reports);
  const { selectedSchool, tariff } = useTypedSelector((state) => state.currentSchool);
  const { isContact, isAdmin, messenger_connect, require_messenger } = useTypedSelector((state) => state.currentUser);

  const dispatch = useAppDispatch();
  const {
    getSavedReports,
    setActiveMainTab,
    setAppReportOpen,
    setDrawerLink,
    postReport,
  } = useActions();

  const [isSideMenuOpen, setIsSideMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [activeContentTab, setActiveContentTab] = useState<ActiveContentTab>('all');
  const [dynamicOpen, setDynamicOpen] = useState(false);

  const navRef = useRef<HTMLDivElement>(null);

  const location = useLocation();
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  useEffect(() => {
    const matchedEntry = pathToKeyMap.find(([path]) =>
      location.pathname.startsWith(path)
    );
    if (
      location.pathname.startsWith(routeNames.adsConnect) ||
      location.pathname.startsWith(routeNames.adsSpend) ||
      location.pathname.startsWith(routeNames.adsRules)
    ) {
      setActiveTab('all');
      closeAdMenu();
    }

    if (matchedEntry) {
      setActiveMainTab(matchedEntry[1]);
      closeAllMenus();
    }

    if (
      searchParams.get(QUERY_NAME_DASHBOARD) &&
      location.pathname.startsWith(routeNames.dashboards)
    ) {
      const fromMenu = searchParams.get(QUERY_FROM_MENU);
      setActiveMainTab(fromMenu ? 'overview' : 'dashboards');
    }
  }, [location.pathname]);

  useEffect(() => {
    if (selectedSchool?.id) {
      getSavedReports(selectedSchool.id);
    }
  }, [selectedSchool?.id]);

  useEffect(() => {
    dispatch(fetchTags());
  }, [dispatch]);

  useEffect(() => {
    setActiveContentTab('all');
  }, []);

  const handleOpenMainDashboard = () => {
    // Без привязанного мессенджера дашборд не открываем — сначала страница привязки
    if (require_messenger && !messenger_connect) return;
    // if (!(selectedSchool?.id)) return;
    if (mainDashboardHash) {
      const query = new URLSearchParams([
        [QUERY_NAME_DASHBOARD, mainDashboardHash],
        [QUERY_FROM_MENU, 'true'],
      ]);
      const location = `${routeNames.dashboards}?${query.toString()}`;
      navigate(location);
      closeAllMenus();
    } else {
      showInfoNotification('Добавьте дашборд в сводку');
    }
  };
  const handleOpenHelpFrame = (url: string) => {
    const [base, hash] = url.split('#');
    const linkWithTimestamp = `${base}?timestamp=${new Date().getTime()}${hash ? `#${hash}` : ''}`;
    setDrawerLink(linkWithTimestamp);
    setAppReportOpen(true);
    closeAllMenus();
  };
  const closeAllMenus = () => {
    setDynamicOpen(false);
    setIsSideMenuOpen(false);
  };
  const handleMainTabClick = (tab: typeof activeMainTab) => {
    if (activeMainTab === tab) {
      setIsSideMenuOpen((prev) => !prev);
    } else {
      closeAllMenus();
      setActiveMainTab(tab);
      setIsSideMenuOpen(true);
    }
  };

  const handleMenuSelect = (
    tab: MenuTab,
    onChangeVisible?: (visible: boolean) => void
  ) => {
    if (activeMainTab !== tab && dynamicOpen) return;

    setDynamicOpen((prev) => {
      onChangeVisible?.(prev);
      return !prev;
    });
  };

  const handleAdTabClick = () => {
    setIsSideMenuOpen(false);
    setActiveTab('cabinets');
    handleMenuSelect('ads');
  };
  const handleBdTabClick = () => {
    setIsSideMenuOpen(false);

    setActiveTab('platform');
    handleMenuSelect('bd');
  };
  const handleToolsTabClick = () => {
    setIsSideMenuOpen(false);
    handleMenuSelect('tools');
  };
  const handleSettingsTabClick = () => {
    setIsSideMenuOpen(false);
    setActiveTab('integrations');
    handleMenuSelect('settings');
  };
  const closeSideMenu = () => {
    setIsSideMenuOpen(false);
  };
  const closeAdMenu = () => {
    setDynamicOpen(false);
    setActiveTab('all');
  };

  const reportRouteMap: Record<DatePeriodType, string> = {
    registrations: routeNames.home,
    orders: routeNames.orderReports,
    payments: routeNames.paymentReports,
    webinars: routeNames.webinarReports,
    ads: routeNames.adReports,
  };

  const handleCreateReport = (tab: DatePeriodType) => {
    const route = reportRouteMap[tab];
    navigate(route);
    closeSideMenu();
  };

  const handleCreateDashboard = () => {
    if (!selectedSchool?.id) return;
    const values = {
      name: 'Новый дашборд',
      blocks: [],
      segment: null,
      filters: [],
      period: {
        start: dayjs().format('YYYY-MM-DD'),
        end: dayjs().format('YYYY-MM-DD'),
        interval: 'day',
        select: 'today',
      },
    };
    const onSuccess = (hash: string) => {
      navigate(`${routeNames.dashboards}?${QUERY_NAME_DASHBOARD}=${hash}`);
    };
    postReport(selectedSchool.id, { values, onSuccess });
    closeSideMenu();
  };

  const transformedData = useMemo(() =>
    savedReports?.map((report) => ({
      id: report.id,
      name: report.name || 'Без названия',
      hash: report.hash,
      type: report.access_id,
      report_id: report.report_id,
      tags: report.tags
        ? Array.isArray(report.tags)
          ? report.tags.map((t: any) => ({
            id: Number(t.id),
            name: String(t.name),
          }))
          : Object.entries(report.tags).map(([id, name]) => ({
            id: Number(id),
            name: String(name),
          }))
        : [],
      category: (() => {
        switch (report.report_id) {
          case 1:
            return 'registrations';
          case 2:
            return 'orders';
          case 3:
            return 'payments';
          case 4:
            return 'ads';
          case 5:
            return 'webinars';
          default:
            return 'registrations';
        }
      })(),
      is_favorite: report.is_favorite,
      values: report.values || {},
    })) || [],
  [savedReports]);

  const transformedDashboardsData = useMemo(() => {
    const parseTags = (tags: any) => {
      if (Array.isArray(tags)) {
        return tags.map((t) => ({ id: t.id, name: t.name }));
      }
      if (typeof tags === 'object' && !Array.isArray(tags)) {
        return Object.entries(tags).map(([id, name]) => ({
          id: Number(id),
          name: String(name),
        }));
      }
      return [];
    };

    return savedDashboards?.map((dashboard) => ({
      id: dashboard.id,
      name: dashboard.name || 'Без названия',
      type:
        dashboard.access_id === 2
          ? 'школа'
          : dashboard.access_id === 3
            ? 'SCAN'
            : 'my',
      tags: parseTags(dashboard.tags),
      is_favorite: dashboard.is_favorite,
      report_id: null,
      hash: null,
      values: dashboard.values || {},
    })) || [];
  }, [savedDashboards]);

  const items: (Omit<MenuItemType, 'key'> & { key: MenuTab })[] = [
    ...(selectedSchool?.id ? [{
      key: 'overview' as MenuTab,
      label: (
        <div data-help-id="nav-overview" className={styles.navLabel}>
          Сводка
        </div>
      ),
      icon: <img className={styles.navIcon} alt="overview" src="/overview.png" />,
    }] : []),

    {
      key: 'dashboards',
      label: (
        <div data-help-id="nav-dashboards" className={styles.navLabel}>
          Дашборды
        </div>
      ),
      icon: <img className={styles.navIcon} alt="dashboards" src="/dashboard.png" />,
    },
    {
      key: 'reports',
      label: (
        <div data-help-id="nav-reports" className={styles.navLabel}>
          Отчёты
        </div>
      ),
      icon: <img className={styles.navIcon} alt="reports" src="/chart.png" />,
    },
    {
      key: 'ads',
      label: (
        <div data-help-id="nav-ads" className={styles.navLabel}>
          Реклама
        </div>
      ),
      icon: <img className={styles.navIcon} alt="ads" src="/ads.png" />,
    },
    {
      key: 'bd',
      label: (
        <div data-help-id="nav-bd" className={styles.navLabel}>
          Данные
        </div>
      ),
      icon: <img className={styles.navIcon} alt="database" src="/bd.png" />,
    },
    {
      key: 'tools',
      label: (
        <div data-help-id="nav-tools" className={styles.navLabel}>
          Инструменты
        </div>
      ),
      icon: <img className={styles.navIcon} alt="tools" src="/tools.png" />,
    },
    {
      key: 'settings',
      label: (
        <div data-help-id="nav-settings" className={styles.navLabel}>
          Настройка
        </div>
      ),
      icon: <img className={styles.navIcon} alt="settings" src="/settings.png" />,
    },
    {
      key: 'suppliers',
      label: (
        <Link data-help-id="nav-suppliers" className={styles.navLabel} to={routeNames.partner}>
          Партнёрка
        </Link>
      ),
      icon: <img className={styles.navIcon} alt="suppliers" src="/suppl.png" />,
    },
  ];

  const adMenuItems = [
    {
      key: 'ad-accounts-helper',
      label: (
        <HelpWrapper>
          Как настроить рекламу?
        </HelpWrapper>
      ),
      isHelper: true,
      onClick: () =>
        handleOpenHelpFrame(
          'https://doc.scananalytics.ru/reklama/'
        ),
    },
    {
      key: 'cabinets',
      label: 'Кабинеты',
      onClick: () => setActiveTab('cabinets'),
      withoutClose: true,
    },
    {
      key: 'announcements',
      label: 'Объявления',
      onClick: () => setActiveTab('announcements'),
      withoutClose: true,
    },
    {
      key: 'ads-budget-optimizer',
      label: 'Оптимизатор бюджета',
      onClick: () => {
        navigate(routeNames.adsRules);
      },
    },
  ];

  const bdMenuItems = [
    {
      key: 'platform',
      label: 'Платформа',
      onClick: () =>
        setActiveTab('platform'),
      withoutClose: true,
    },
    {
      key: 'webinars',
      label: 'Вебинары',
      onClick: () =>
        setActiveTab('webinars'),
      withoutClose: true,
    },
    {
      key: 'visits',
      label: 'Посещения',
      onClick: () =>
        setActiveTab('visits'),
      withoutClose: true,
    },
    {
      key: 'import',
      label: 'Импорт',
      url: routeNames.import,
      permission: isAdmin ? [ROLES.owner, ROLES.employee] : [ROLES.owner],
      locked: isImportBlockedByTariff(tariff?.id),
      lockedMessage: 'Раздел «Импорт» недоступен на вашем тарифе.',
    },
    {
      key: 'export',
      label: 'Экспорт',
      url: routeNames.export,
      // Доступен владельцу и любому сотруднику (без привязки к доступу к контактам)
      permission: [ROLES.owner, ROLES.employee],
    },
    {
      key: 'add-data',
      label: 'Ручная загрузка данных',
      url: routeNames.schoolLoadData,
      permission: [ROLES.owner, ROLES.employee],
    },
  ];

  const toolsMenuItems = [
    {
      key: 'yandex-metric',
      label: 'Яндекс.Метрика (конверсии)',
      url: `${routeNames.yandexMetrics}?tab=1`,
    },
    {
      key: 'reportTg',
      label: 'Telegram. Радар',
      onClick: () =>
        handleOpenHelpFrame(
          'https://doc.scananalytics.ru/instrumenty/telegram-radar/'
        ),
    },
  ];

  const settingsMenuItems = [
    {
      key: 'integrations',
      label: 'Интеграции',
      onClick: () => {
        setActiveTab('integrations');
      },
      withoutClose: true,
    },
    {
      key: 'school-info',
      label: 'Настройка школы',
      url: routeNames.schoolEdit,
      permission: [ROLES.owner, ROLES.employee],
    },
    {
      key: 'sell',
      label: 'Воронки продаж',
      onClick: () =>
        handleOpenHelpFrame('https://doc.scananalytics.ru/nastrojka/voronki-prodazh/'),
    },
    {
      key: 'tracking',
      label: 'Скрипт отслеживания',
      url: routeNames.schoolTrackScript,
      permission: [ROLES.owner, ROLES.employee],
    },
    {
      key: 'employees',
      label: 'Сотрудники',
      url: routeNames.schoolStaff,
      permission: [ROLES.owner, ROLES.employee],
    },
  ];


  const baseDynamicProps = {
    activeMainTab,
    activeTab,
    setActiveTab,
    collapsed,
  };

  const dynamicsProps: Record<
    DynamicTabs,
    Omit<
      DynamicMenuProps,
      'activeMainTab' | 'activeTab' | 'setActiveTab' | 'collapsed' | 'isOpen'
    >
  > = {
    ads: {
      items: adMenuItems,
      onClose: closeAdMenu,
      nextLevel: {
        items: getAdsLinks(activeTab) ?? undefined,
      },
    },
    bd: {
      items: bdMenuItems,
      onClose: () => setDynamicOpen(false),
      nextLevel: {
        items: getBdLinks(activeTab, isContact) ?? undefined,
      },
    },
    tools: {
      items: toolsMenuItems,
      onClose: () => setDynamicOpen(false),
    },
    settings: {
      items: settingsMenuItems,
      onClose: () => setDynamicOpen(false),
      nextLevel: {
        items: getIntegrationLinks(activeTab) ?? undefined,
      },
    },
  };
  const Dynamic = isDynamicTab(activeMainTab) ? (
    <DynamicMenu
      isOpen={dynamicOpen}
      {...dynamicsProps[activeMainTab]}
      {...baseDynamicProps}
    />
  ) : null;

  return (
    <div className={styles.siderChildren} ref={navRef}>
      <div className={styles.navMenu}>
        <Menu
          mode="inline"
          className={styles.navTabs}
          items={items}
          selectedKeys={[activeMainTab]}
          onClick={({ key }) => {
            const menuKey = key as MenuTab;
            switch (menuKey) {
              case 'overview':
                handleOpenMainDashboard();
                return;
              case 'dashboards':
              case 'reports':
                setActiveTab('all');
                handleMainTabClick(menuKey);
                return;
              case 'ads':
                handleAdTabClick();
                setActiveMainTab(menuKey);
                return;
              case 'bd':
                handleBdTabClick();
                setActiveMainTab(menuKey);
                return;
              case 'tools':
                handleToolsTabClick();
                setActiveMainTab(menuKey);
                return;
              case 'settings':
                handleSettingsTabClick();
                setActiveMainTab(menuKey);
                return;
              case 'suppliers':
                closeAllMenus();
                setActiveMainTab(menuKey);
                return;
            }
          }}
          style={{
            paddingLeft: '10px',
            paddingRight: '0px',
            marginTop: '1px',
          }}
        />
        <PromoBanner collapsed={collapsed} />
      </div>
      {Dynamic}
      <SideMenu
        isOpen={isSideMenuOpen}
        onClose={closeSideMenu}
        onOpenHelpFrame={handleOpenHelpFrame}
        onCreateReport={handleCreateReport}
        onCreateDashboard={handleCreateDashboard}
        tags={tags}
        {...baseDynamicProps}
      />
      {isSideMenuOpen && (
        <TabContent
          mainTab={activeMainTab}
          subTab={
            activeTab === 'all' || activeTab === 'favorites' ? activeTab : 'all'
          }
          activeContentTab={activeContentTab}
          onContentTabChange={setActiveContentTab}
          activeMainTab={activeMainTab}
          data={
            activeMainTab === 'reports'
              ? transformedData
              : transformedDashboardsData
          }
          activeReportTab={
            activeTab === 'registrations' ||
              activeTab === 'orders' ||
              activeTab === 'payments' ||
              activeTab === 'webinars' ||
              activeTab === 'ads'
              ? activeTab
              : undefined
          }
          activeTagTab={
            activeTab === 'manager' ||
              activeTab === 'traffic' ||
              activeTab === 'funnels' ||
              activeTab === 'contractors'
              ? activeTab
              : undefined
          }
          activeTab={activeTab}
          tags={tags}
          onItemClick={() => {
            closeSideMenu();
          }}
          collapsed={collapsed}
        />
      )}
    </div>
  );
};

export default Nav;
